import React, { useState, useEffect, useContext, useRef } from 'react';
import styled from 'styled-components';

import * as Colors from 'src/styles/colors';
import { Dimensions } from 'react-native-web';
import ResourcesContext from 'src/contexts/ResourcesContext';
import { useNavigate } from 'react-router-dom';
import SkeletonBlock from 'src/elements/Skeleton';
import './carouselStyles.css';

const dimensions = Dimensions.get('window');
const smallScreen = dimensions.width < 800;
const mediumScreen = dimensions.width > 480 && dimensions.width < 1180;
const picWidth = smallScreen ? 100 : 150;
// Scale the scroll duration to item count so speed stays constant regardless of how many arrivals there are.
const SCROLL_SECONDS_PER_ITEM = 2.0;

const Container = styled.div`
  width: 100%;
  padding: 8px 1% 12px;
  background-color: ${Colors.elLightGray};
  max-width: 100%;

  @media (max-width: 768px) {
    margin-top: 15px;
    padding-bottom: 0;
  }
`;

const unSelectedPhoto = {
  flex: 1,
  maxWidth: '150px',
  maxHeight: '150px',
  height: picWidth + 'px',
  width: picWidth + 'px',
  objectFit: 'cover',
  marginTop: '10px',
  padding: '0 10px'
}

const PrimaryCarousel = styled.div`
  display: flex;
  max-width: 100%;
  min-width: fit-content;
  position: relative;
  gap: ${props => `${props.gap}px`};
  animation: scroll-horizontal ${props => props.duration}s linear infinite;
  -webkit-animation-timing-function: linear; /* Chrome, Safari, Opera */
  animation-timing-function: linear;
  @keyframes scroll-horizontal {
    0%{
      left: 0%;
      transform: translateX(30px);
    }

    100% {
      left: ${props => `-${props.offset + (props.gap + smallScreen || mediumScreen ? picWidth + (props.gap/2) :
        props.full ? (picWidth / 2) - ((picWidth/2) + props.gap) : 0)}px`};
      transform: translateX(30px);
    }
  }
`;

const SecondaryCarousel = styled.div`
  display: flex;
  max-width: 100%;
  min-width: fit-content;
  position: relative;
  gap: ${props => `${props.gap}px`};
  animation: scroll-horizontal ${props => props.duration}s linear infinite;
  margin-left: ${props => `${props.gap + (smallScreen || mediumScreen ? picWidth + (props.gap/2) :
    props.full ? (picWidth / 2) - ((picWidth/2) + props.gap) : 0)}px`};
  -webkit-animation-timing-function: linear; /* Chrome, Safari, Opera */
  animation-timing-function: linear;
`;

const placeholderRowStyle = {
  display: 'flex',
  gap: '20px',
  marginTop: '10px',
  paddingLeft: '4%',
  overflow: 'hidden',
};

export const NewArrivalsPlaceholder = () => (
  <Container data-testid="new-arrivals-placeholder">
    <div style={placeholderRowStyle}>
      {[...Array(8).keys()].map(index => (
        <SkeletonBlock
          key={index}
          $width={`${picWidth}px`}
          $height={`${picWidth}px`}
        />
      ))}
    </div>
  </Container>
);

const NewArrivals = ({assets}) => {
  const navigate = useNavigate();
  const scrollContainerRef = useRef();
  const primary = useRef();
  const secondary = useRef();
  const { clearAssets } = useContext(ResourcesContext);
  const [gap, setGap] = useState(0);
  const [brokenIds, setBrokenIds] = useState(new Set());

  const visibleAssets = (assets || []).filter(
    a => a.primaryPhotoUrl && !brokenIds.has(a.id)
  );

  useEffect(() => {
    if(scrollContainerRef?.current) setGap(getGap());
  }, [scrollContainerRef]);

  if(!visibleAssets.length) return null

  const getSlug = asset => (
    asset.name.replace(/[^0-9a-z]+/gi, '-')
  );

  const getGap = () => {
    if (!visibleAssets.length) return 0;
    let totalPicWidth = visibleAssets.length * picWidth;
    return (scrollContainerRef?.current?.clientWidth - totalPicWidth) / visibleAssets.length;
  }

  const getOffset = () => {
    let scrollWidth = scrollContainerRef?.current?.clientWidth || 0;
    let offsetWidth = primary?.current?.clientWidth + (gap * 2);
    if(offsetWidth < scrollWidth) return 0;
    return offsetWidth;
  }

  return (
    <Container>
      <div className="scroll-container" ref={scrollContainerRef}>
        <PrimaryCarousel
          className='carousel-primary'
          offset={getOffset() - gap}
          ref={primary}
          gap={gap}
          duration={(visibleAssets.length || 1) * SCROLL_SECONDS_PER_ITEM}
          full={picWidth * visibleAssets.length > scrollContainerRef?.current?.clientWidth}
        >
          {visibleAssets.map((a, index) => {
            return (
              <div
                key={index}
                onClick={() => navigate(`/inventory/${a.id}/${getSlug(a)}/false`)}
                className='image'
              >
                <img
                  src={a.primaryPhotoUrl}
                  alt={a.name}
                  style={unSelectedPhoto}
                  onError={() => setBrokenIds(prev => new Set(prev).add(a.id))}
                />
              </div>
            )
          })}
        </PrimaryCarousel>
        <SecondaryCarousel
          className={`carousel-primary carousel-secondary`}
          ref={secondary}
          offset={(getOffset() * 2)}
          secondary={true}
          marginLeft={primary?.current?.clientWidth}
          gap={gap}
          duration={(visibleAssets.length || 1) * SCROLL_SECONDS_PER_ITEM}
          full={picWidth * visibleAssets.length > scrollContainerRef?.current?.clientWidth}
        >
          {visibleAssets.map((a, index) => {
            return (
              <div
                key={index}
                onClick={() => navigate(`/inventory/${a.id}/${getSlug(a)}/false`)}
                className='image'
              >
                <img
                  src={a.primaryPhotoUrl}
                  alt={a.name}
                  style={unSelectedPhoto}
                  onError={() => setBrokenIds(prev => new Set(prev).add(a.id))}
                />
              </div>
            )
          })}
        </SecondaryCarousel>
      </div>
    </Container>
  )
}

export default NewArrivals;
